import { useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useUi } from '../../context/UiContext';
import { useI18n } from '../../i18n/useI18n';
import { toKey } from '../../utils/dates';
import { recurrenceLabel } from '../../utils/recurrence';
import { IconClose, IconPlus } from '../icons/Icons';
import styles from './overlays.module.css';

const EXAMPLES = [
  ['recur.freq.day', 'recur.ex.day'],
  ['recur.freq.week', 'recur.ex.week'],
  ['recur.freq.other', 'recur.ex.other'],
  ['recur.freq.month', 'recur.ex.month'],
  ['recur.freq.year', 'recur.ex.year'],
];

const EXIT_MS = 260;

export function RecurringPanel({ open }) {
  const { state, actions } = useApp();
  const { ui, close, openRecurCreate } = useUi();
  const { t } = useI18n();
  const [phase, setPhase] = useState('off');
  const [shown, setShown] = useState(false);
  const phaseRef = useRef('off');
  const items = state.recurring || [];

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    if (open) {
      setShown(true);
      if (reduce || phaseRef.current === 'on') {
        phaseRef.current = 'on';
        setPhase('on');
        return undefined;
      }
      if (phaseRef.current === 'out') {
        phaseRef.current = 'on';
        setPhase('on');
        return undefined;
      }
      phaseRef.current = 'in';
      setPhase('in');
      let inner = 0;
      let cancelled = false;
      const outer = window.requestAnimationFrame(() => {
        inner = window.requestAnimationFrame(() => {
          if (cancelled || phaseRef.current !== 'in') return;
          phaseRef.current = 'on';
          setPhase('on');
        });
      });
      return () => {
        cancelled = true;
        window.cancelAnimationFrame(outer);
        window.cancelAnimationFrame(inner);
      };
    }

    if (phaseRef.current === 'off') return undefined;
    if (reduce) {
      phaseRef.current = 'off';
      setPhase('off');
      setShown(false);
      return undefined;
    }
    phaseRef.current = 'out';
    setPhase('out');
    const timer = window.setTimeout(() => {
      phaseRef.current = 'off';
      setPhase('off');
      setShown(false);
    }, EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open || ui.recurCreate) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape' || document.body.classList.contains('is-dragging')) return;
      event.preventDefault();
      close('recurringOpen');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, ui.recurCreate, close]);

  if (!open && !shown) return null;
  const shownPhase = phase === 'off' ? 'in' : phase;

  const create = () => {
    openRecurCreate({ text: '', every: 1, unit: 'day', start: toKey(new Date()) });
  };

  return (
    <>
      <div className={styles.recurScrim} data-phase={shownPhase} onClick={() => close('recurringOpen')} />
      <aside
        className={styles.recurPanel}
        data-phase={shownPhase}
        role="dialog"
        aria-label={t('recur.title')}
      >
        <header className={styles.recurHead}>
          <h2>{t('recur.title')}</h2>
          <button type="button" className={styles.recurClose} aria-label={t('recur.close')} onClick={() => close('recurringOpen')}>
            <IconClose />
          </button>
        </header>
        {items.length === 0 ? (
          <div className={styles.recurEmpty}>
            <p>{t('recur.empty')}</p>
            <button type="button" className={styles.recurAdd} onClick={create}>
              <IconPlus />
              {t('recur.add')}
            </button>
            <p className={styles.recurHint}>{t('recur.hint')}</p>
            <div className={styles.recurExamples}>
              <div>
                <p>{t('recur.frequencies')}</p>
                {EXAMPLES.map(([freq]) => (
                  <span key={freq}>{t(freq)}</span>
                ))}
              </div>
              <div>
                <p>{t('recur.examples')}</p>
                {EXAMPLES.map(([, example]) => (
                  <span key={example}>{t(example)}</span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.recurList}>
            <button type="button" className={styles.recurAdd} onClick={create}>
              <IconPlus />
              {t('recur.add')}
            </button>
            <ul>
              {items.map((item) => (
                <li key={item.id}>
                  <button type="button" className={styles.recurItem} onClick={() => openRecurCreate(item)}>
                    <strong dir="auto">{item.text}</strong>
                    <span>{recurrenceLabel(item, t)}</span>
                  </button>
                  <button
                    type="button"
                    className={styles.recurDelete}
                    aria-label={t('task.delete')}
                    onClick={() => actions.deleteTask({ zone: 'day', date: item.start }, item.id)}
                  >
                    <IconClose width="14" height="14" />
                  </button>
                </li>
              ))}
            </ul>
            <p className={styles.recurHint}>{t('recur.hint')}</p>
          </div>
        )}
      </aside>
    </>
  );
}
